import * as React from 'react';
import * as cx from 'classnames';
import { SuperCriticalComponent } from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';

import { PCTabStrip, TitledTab } from './model';

export interface PCTabStripViewController<TTab> {
    renderTabButtonContents(tab : TTab) : React.ReactElement<any> | null;
}

export class TitledTabStripViewController<TTab extends TitledTab = TitledTab> implements PCTabStripViewController<TTab> {
    renderTabButtonContents(tab : TTab) {
        return <span>{tab.Title}</span>;
    }
}

export interface PCTabStripViewProps<TTab> {
    model : PCTabStrip<TTab>;
    ctrl : PCTabStripViewController<TTab>;
    extraContents? : () => React.ReactElement<any> | null
}

export class PCTabStripView<TTab> extends SuperCriticalComponent<PCTabStripViewProps<TTab>> {
    srender({ model: m, ctrl, extraContents } : PCTabStripViewProps<TTab>) {
        const s = m.State();
        return (
            <div className="pctabstrip">
                <span className="anchor-tab">
                    <TabButton
                        model={m}
                        ctrl={ctrl}
                        tab={
                            s.state === 'settled-on-front' ? m.FrontTab :
                            s.state === 'settled-on-back' ? s.tab :
                            s.state === 'flipping-to-front' ? s.fromTab :
                            s.state === 'flipping-to-back' ? m.FrontTab :
                            null!
                        }
                        active
                    />
                </span>
                <span className="remaining-tabs">
                    {(s.state === 'settled-on-front' || s.state === 'flipping-to-back') && m.BackTabs().map((t, i) =>
                        <TabButton
                            key={i}
                            model={m}
                            ctrl={ctrl}
                            tab={t}
                        />
                    )}
                </span>
                <span className="extra-content">{extraContents?.()}</span>
            </div>
        );
    }
}

interface TabButtonProps<TTab> {
    model : PCTabStrip<TTab>;
    ctrl : PCTabStripViewController<TTab>;
    tab : TTab;
    active? : boolean;
}

class TabButton<TTab> extends SuperCriticalComponent<TabButtonProps<TTab>> {
    srender({ model: m, tab: t, ctrl, active } : TabButtonProps<TTab>) {
        return (
            <button
                className={cx("tab-btn", { "tab-btn-active": active })}
                onClick={noDefault(() => m.FlipToTab(t))}
            >
                {ctrl.renderTabButtonContents(t)}
            </button>
        );
    }
}

export interface PCTabContentsController<TTab> {
    renderTabContents(tab : TTab) : React.ReactElement<any> | null;
    renderBackToFrontLinkContents(frontTab : TTab) : React.ReactElement<any> | null;
}

export abstract class TitledTabContentsController<TTab extends TitledTab = TitledTab> implements PCTabContentsController<TTab> {
    abstract renderTabContents(tab : TTab) : React.ReactElement<any> | null;

    renderBackToFrontLinkContents(frontTab : TTab) {
        return <span><span className="fa fa-arrow-left" /> back to {frontTab.Title}</span>;
    }
}

export interface PCTabContentsProps<TTab> {
    model : PCTabStrip<TTab>;
    ctrl : PCTabContentsController<TTab>;
}

export class PCTabContents<TTab> extends SuperCriticalComponent<PCTabContentsProps<TTab>> {
    srender({ model: m, ctrl } : PCTabContentsProps<TTab>) {
        const s = m.State();
        return (
            <div className="pctabcontents">
                <div
                    className={cx('flip-container', {
                        flipping: s.state === 'flipping-to-back' || s.state === 'flipping-to-front',
                        'reverse-flip': s.state === 'flipping-to-front'
                    })}
                >
                    {s.state !== 'settled-on-back' &&
                        <div className="flip-face-front">{ctrl.renderTabContents(m.FrontTab)}</div>
                    }
                    {s.state !== 'settled-on-front' &&
                        <div className="flip-face-back">
                            <div className="back-to-front-shell">
                                <a href="" onClick={noDefault(() => m.FlipToTab(m.FrontTab))}>
                                    {ctrl.renderBackToFrontLinkContents(m.FrontTab)}
                                </a>
                            </div>
                            <div>
                                {ctrl.renderTabContents(
                                    s.state === 'flipping-to-back' ? s.toTab :
                                    s.state === 'flipping-to-front' ? s.fromTab :
                                    s.tab
                                )}
                            </div>
                        </div>
                    }
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/pctabstrip/view.tsx